import React, { useState, useEffect, useRef } from "react";

import cx from "classnames";

import { Popper } from "../Popper";

import { QuestionMarkTooltipIcon } from "./QuestionMarkTooltipIcon";
import styles from "./Tooltip.scss";
import { TooltipContent } from "./TooltipContent";

/**
 * Props for the Tooltip component.
 */
export interface TooltipProps {
  /**
   * Contents of the tooltip panel
   */
  content: React.ReactNode;
  /**
   * The element that needs a tooltip
   */
  children?: React.ReactElement | null;
  /**
   * Class of the base element
   */
  className?: string;
  /**
   * Class name of the tooltip panel
   */
  tooltipClassName?: string;
  /**
   * Whether or not to use the `?` icon
   */
  icon?: boolean;
  /**
   * Whether the tooltip hangs left
   */
  hangLeft?: boolean;
  /**
   * Tooltip will appear on hover
   */
  showOnHover?: boolean;
  /**
   * Id of the tooltip
   */
  id?: string;
  /**
   * Ref of the tooltip
   */
  innerRef?: React.Ref<HTMLDivElement>;
  /**
   * OnClick handler passed to the tooltip wrapper div
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

/**
 * Tooltip component that displays additional information when hovering or clicking on an element.
 *
 * ## Usage
 *
 * ### Simple tooltips
 * For extremely simple tooltips, you only need to provide any element a `data-tooltip`!
 *
 * ```tsx
 * <Link to="delete" data-tooltip aria-label="Delete this Item">
 *   Delete
 * </Link>
 * ```
 *
 * ### Advanced tooltips
 * Sometimes, tooltips need more than just simple text. To do that, use the `Tooltip` component:
 *
 * ```tsx
 * <Tooltip content={<ToolTipContents />}>
 *   <Button>Do Something Advanced</Button>
 * </Tooltip>
 * ```
 *
 * The tooltip fades in quickly, but fades out slowly for users with disabilities.
 * It also has focus states.
 */
export function Tooltip({
  innerRef,
  content,
  children = null,
  className,
  tooltipClassName,
  icon = false,
  hangLeft = false,
  showOnHover = false,
  id,
  ...elementProps
}: TooltipProps &
  Omit<React.HTMLAttributes<HTMLDivElement>, "onClick" | "content">) {
  const uuid = React.useId();
  const tooltipId = id || uuid;
  const [active, setActive] = useState(false);
  const tooltipIconRef = useRef<HTMLButtonElement>(null);
  const hasIcon = icon;

  const handleClickOutside = (e: MouseEvent): void => {
    // Let "tooltip-help-icon" onClick handle the setActive change if icon is clicked while open
    if (e.target !== tooltipIconRef.current) {
      setActive(false);
    }
  };

  const handleTypeEscape = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && active) {
      setActive(false);
    }
  };

  useEffect(() => {
    if (active) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [active]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      handleTypeEscape(e);
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [active]);

  const disappearOnHover = showOnHover ? false : active;
  const appearOnHover = showOnHover ? true : active;

  return (
    <div
      className={cx(styles.tooltip, className)}
      data-testid="tooltip"
      {...elementProps}
    >
      <Popper
        closeOnClickAway={active}
        onSetOpen={active ? () => setActive(false) : null}
        isOpen={active}
        popupElement={
          <TooltipContent
            id={tooltipId}
            hangLeft={hangLeft}
            tooltipClassName={tooltipClassName}
            content={content}
          />
        }
      >
        {hasIcon && (
          <QuestionMarkTooltipIcon
            tooltipIconRef={tooltipIconRef}
            active={active}
            setActive={setActive}
            onMouseLeave={() => setActive(disappearOnHover)}
            onMouseEnter={() => setActive(appearOnHover)}
          />
        )}
        {!hasIcon && (
          // This prevents existing tooltips from breaking:
          <div
            className={cx(styles["tooltip"], className)}
            onMouseEnter={() => setActive(true)}
            onMouseLeave={() => setActive(false)}
            onFocus={() => setActive(true)}
            onBlur={() => setActive(false)}
            tabIndex={0}
            aria-describedby={tooltipId}
            ref={innerRef}
          >
            {children}
          </div>
        )}
      </Popper>
    </div>
  );
}
